import { Component, ViewChild, ElementRef, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, Inject, forwardRef, ChangeDetectorRef } from '@angular/core';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { QuestionCardComponent } from '../../../../shared/components/question-card/question-card';
import { FeedbackCardComponent } from '../feedback-card/feedback-card';
import { CollectionsComponent } from '../collections/collections.component';
import { FeedbackAdminComponent } from '../feedback-admin/feedback-admin.component';
import { MailboxPageComponent } from '../../../mailbox/mailbox-page/mailbox-page';
import { VisualizationComponent } from '../visualizations/visualization.component';
import { InferencesComponent } from '../inferences/inferences.component';
import { ClustersComponent } from '../clusters/clusters.component';
import { MembersComponent } from '../members/members.component';
import { SUGGESTED_QUESTIONS } from '../../constants/chat.constants';
import { ChatService } from '../../services/chat.service';
import { UserQueryModel, ChatBotResponse } from '../../interfaces/user-query.interface';
import { ChatHistoryMessage, mapChatHistoryMessage } from '../../interfaces/chat-history-response.interface';
import { IChatMessage, IToolStep } from '../../../../shared/interfaces/chat.interface';
import { UpdateChatModel, UpdateChatResponse } from '../../interfaces/update-chat.interface';
import { MarkdownPipe } from '../../../../shared/pipes/markdown.pipe';
import { FeedbackData, FeedbackRequestModel } from '../../interfaces/feedback.interface';
import { ImgPopup } from '../../../../shared/components/img-popup/img-popup';
import { ModalService } from '../../services/modal.service';
import { AuthService } from '../../../../shared/services/auth.service';
import { TenantService } from '../../../../shared/services/tenant.service';
import { ChatbotStreamRequest } from '../../interfaces/chatbot-stream-request.interface';
import { DocumentService } from '../../services/document.service';
import { VoiceNotePlayerComponent, stopVoiceNotePlayback } from '../voice-note-player/voice-note-player.component';
import { flatWave, voiceBarHeight, VoiceRecorder, VoiceRecorderError } from '../../services/voice-recorder';
import { VOICE_NOTE_LABELS } from '../../constants/voice-note.constants';
import { VoiceRecording } from '../../interfaces/voice-note.interface';

@Component({
  selector: 'app-chat-area',
  standalone: true,
  imports: [CommonModule, FormsModule, QuestionCardComponent, FeedbackCardComponent, CollectionsComponent, FeedbackAdminComponent, MailboxPageComponent, VisualizationComponent, InferencesComponent, ClustersComponent, MembersComponent, MarkdownPipe, ImgPopup, VoiceNotePlayerComponent],
  templateUrl: './chat-area.html',
  styleUrls: ['./chat-area.css']
})
export class ChatAreaComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('messagesContainer') messagesContainer!: ElementRef<HTMLDivElement>;

  @Input() showCollections = false;
  @Input() showFeedbackAdmin = false;
  @Input() showMailbox = false;
  @Input() showVisualizations = false;
  @Input() showInferences = false;
  @Input() showClusters = false;
  @Input() showMembers = false;
  @Input() isSidebarCollapsed = false;
  @Output() backToChat = new EventEmitter<void>();
  @Output() chatSaved = new EventEmitter<{ chatId: string; label: string }>();
  @Output() inferencesGenerated = new EventEmitter<void>();

  currentMessage = '';
  hasMessages = false;
  messages: IChatMessage[] = [];
  private queuedMessage: { userQuery: string; userMessageIndex: number } | null = null;

  suggestedQuestions: string[] = [];
  isLoading = false;
  currentChatId: string | null = null; // Track current chat ID
  currentChatLabel: string | null = null; // Track current chat label
  // Dynamic thinking messages
  thinkingMessages = ['Analyzing', 'Fetching data', 'Creating result'];
  currentThinkingMessage = 'Analyzing';
  private thinkingInterval: any = null;

  private subscriptions: Subscription[] = [];
  private destroy$ = new Subject<void>();

  showFeedbackCard = false;
  feedbackType: 'positive' | 'negative' = 'positive';
  feedbackMessageId: string | null = null;

  showScrollToBottom = false;
  private userAtBottom = true;
  private activeStreamSubscription: Subscription | null = null;
  private isCurrentTurnNewChat = false;
  private currentTurnUserQuery = '';
  previewImage: string | null = null;
  openUp = false;
  selectedImage: string | null = null;
  ChatLable: string | null = null;
  openSourcesMessageId: string | null = null;
  private secureImageUrlCache = new Map<string, string>();
  private secureImageRequestCache = new Map<string, Promise<string>>();
  private secureImageRequestToken = 0;
  private voiceObjectUrls: string[] = [];
  private voiceLoadToken = 0;
  private voiceRecorder = new VoiceRecorder();
  private voiceStartPending = false;
  private persistVoiceAsUpdate = false;
  isRecording = false;
  isUploadingVoice = false;
  voiceError: string | null = null;
  recordSeconds = 0;
  liveBars: number[] = flatWave();
  readonly voiceLabels = VOICE_NOTE_LABELS;

  constructor(
    private chatService: ChatService,
    private documentService: DocumentService,
    private cdr: ChangeDetectorRef,
    private modalService: ModalService,
    private authService: AuthService,
    private tenantService: TenantService
  ) { }
  openManualDocumentsModal(): void {
    this.modalService.openManualDocumentsModal().catch(() => { });
  }

  openPromptsGalleryModal(): void {
    this.modalService.openPromptsGalleryModal().then((selectedText: string) => {
      if (selectedText) {
        this.currentMessage = selectedText;
        setTimeout(() => this.messageInput?.nativeElement?.focus(), 0);
      }
    }).catch(() => { });
  }

  private startThinkingCycle(): void {
    let messageIndex = 0;
    this.currentThinkingMessage = this.thinkingMessages[messageIndex];

    this.thinkingInterval = setInterval(() => {
      messageIndex++;
      // Stop cycling when we reach the last message ("Creating result")
      if (messageIndex < this.thinkingMessages.length) {
        this.currentThinkingMessage = this.thinkingMessages[messageIndex];
      } else {
        // Stop the interval when we reach "Creating result" but keep the message
        this.stopThinkingCycle(false);
      }
    }, 4000);
  }

  private extractImageUrls(text: string): string[] {
    const regex = /<img\s+src=["']([^"']+)["'][^>]*\/?>/g;

    const urls: string[] = [];
    let match;

    while ((match = regex.exec(text)) !== null) {
      urls.push(match[1]);
    }

    return urls;
  }

  private removeImageTags(text: string): string {
    return text.replace(/<img\s+src=["'][^"']+["'][^>]*\/?>/g, '');
  }

  private revokeSecureImageUrls(): void {
    this.secureImageRequestToken++;
    this.secureImageUrlCache.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
    this.secureImageUrlCache.clear();
    this.secureImageRequestCache.clear();
  }

  private async resolveSecureImageUrl(imageUrl: string): Promise<string> {
    if (!imageUrl?.trim()) {
      return '';
    }

    const cachedUrl = this.secureImageUrlCache.get(imageUrl);
    if (cachedUrl) {
      return cachedUrl;
    }

    const inFlightRequest = this.secureImageRequestCache.get(imageUrl);
    if (inFlightRequest) {
      return inFlightRequest;
    }

    const requestToken = this.secureImageRequestToken;

    let request!: Promise<string>;
    request = firstValueFrom(this.documentService.getSecureImageBlob(imageUrl))
      .then((blob) => {
        if (requestToken !== this.secureImageRequestToken) {
          if (this.secureImageRequestCache.get(imageUrl) === request) {
            this.secureImageRequestCache.delete(imageUrl);
          }
          return '';
        }

        const objectUrl = URL.createObjectURL(blob);
        this.secureImageUrlCache.set(imageUrl, objectUrl);
        if (this.secureImageRequestCache.get(imageUrl) === request) {
          this.secureImageRequestCache.delete(imageUrl);
        }
        return objectUrl;
      })
      .catch((error) => {
        if (this.secureImageRequestCache.get(imageUrl) === request) {
          this.secureImageRequestCache.delete(imageUrl);
        }
        console.error('Failed to load secure image:', imageUrl, error);
        return '';
      });

    this.secureImageRequestCache.set(imageUrl, request);
    return request;
  }

  private stopThinkingCycle(resetMessage: boolean = true): void {
    if (this.thinkingInterval) {
      clearInterval(this.thinkingInterval);
      this.thinkingInterval = null;
    }
    if (resetMessage) {
      this.currentThinkingMessage = 'Analyzing';
    }
  }
  private getRandomSuggestedQuestions(): string[] {
    // Shuffle the array and pick 3 unique questions
    return [...SUGGESTED_QUESTIONS]
      .sort(() => Math.random() - 0.5)
      .slice(0, 3);
  }

  ngOnInit(): void {
    // Initial selection is driven by sidebar startup policy.
    // This avoids stale active-chat restore on hard reload.
    this.suggestedQuestions = this.getRandomSuggestedQuestions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['showCollections'] && !changes['showCollections'].currentValue) {
      setTimeout(() => {
        this.scrollToBottom();
      }, 0);
    }
  }

  private loadActiveChatHistory(): void {
    const activeChatSubscription = this.chatService.getActiveChatHistory().subscribe({
      next: (response: any) => {
        if (response && response.isSuccess && response.data) {
          // Parse the messages and load them into chat area
          try {
            const messages = JSON.parse(response.data.messagesJson);
            const chatHistoryMessages: ChatHistoryMessage[] = messages.map((msg: any) => mapChatHistoryMessage(msg));

            // Load the active chat history
            this.loadChatFromHistory(chatHistoryMessages, response.data.chatId);

            // Set the current chat label
            this.currentChatLabel = response.data.chatLabel;
          } catch (error) {
            console.error('Error parsing active chat history messages:', error);
          }
        }
        // If no active chat found, do nothing (as requested)
      },
      error: (error: any) => {
        console.error('Error loading active chat history:', error);
      }
    });

    this.subscriptions.push(activeChatSubscription);
  }
  checkPosition(): void {
    const cardEl = document.querySelector('.feedback-card-wrapper') as HTMLElement;
    if (!cardEl) return;

    const rect = cardEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // if not enough space below -> open upwards
    this.openUp = rect.bottom + 200 > viewportHeight; // 200 = approx card height
  }
  showNotificationBar = false;
  notificationMessage = '';
  notificationType: 'warning' | 'success' = 'success';
  showWarningMessage = false;

  showNotificationsBar(): void {
    // Disabled startup warning banner.
  }

  dismissNotificationBar(): void {
    this.showNotificationBar = false;
  }

  showFeedbackSuccessNotification(): void {
    this.notificationMessage = '🎉 Thanks for your feedback!';
    this.notificationType = 'success';
    this.showNotificationBar = true;
    this.showWarningMessage = false;

    setTimeout(() => {
      this.showNotificationBar = false;
    }, 3000);
  }

  scrollToBottom(): void {
    setTimeout(() => {
      if (this.messagesContainer && this.messagesContainer.nativeElement) {
        const container = this.messagesContainer.nativeElement;
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
        this.userAtBottom = true;
        this.showScrollToBottom = false;
      }
    }, 100);
  }

  /** Only follows new content to the bottom while the user hasn't scrolled away to read something earlier. */
  private followScrollIfAtBottom(): void {
    if (this.userAtBottom) {
      this.scrollToBottom();
    }
  }

  /** Stops the response currently streaming in, keeping whatever text has arrived so far. */
  stopGenerating(): void {
    this.finalizeInterruptedResponse();
    this.trySendQueuedMessage();
  }

  /**
   * A message typed while a response is still streaming appears in the transcript
   * immediately, like any other sent message - but the actual request for it only
   * fires once the response ahead of it finishes (or is stopped), one at a time,
   * the same way Claude's chat behaves.
   */
  queueMessage(): void {
    const typedText = this.currentMessage.trim();
    if (!typedText) return;

    const userMessage: IChatMessage = {
      id: Date.now().toString(),
      text: this.currentMessage,
      isUser: true,
      timestamp: new Date()
    };

    this.messages.push(userMessage);
    this.hasMessages = true;
    const userMessageIndex = this.messages.length - 1;
    const userQuery = this.currentMessage;
    this.currentMessage = '';
    setTimeout(() => this.adjustTextareaHeight(), 0);

    this.userAtBottom = true;
    this.scrollToBottom();

    this.queuedMessage = { userQuery, userMessageIndex };
  }

  private trySendQueuedMessage(): void {
    if (!this.queuedMessage || this.isLoading) return;

    const queued = this.queuedMessage;
    this.queuedMessage = null;
    this.dispatchAIResponse(queued.userQuery, queued.userMessageIndex);
  }

  /**
   * Cancels any in-flight response stream and finalizes its message in place. Called before
   * starting a new chat or switching to a different one, so a stale stream can never keep writing
   * into (and saving onto) a conversation it no longer belongs to.
   */
  private finalizeInterruptedResponse(): void {
    if (!this.activeStreamSubscription) {
      return;
    }

    this.activeStreamSubscription.unsubscribe();
    this.activeStreamSubscription = null;
    this.isLoading = false;
    this.stopThinkingCycle();

    const aiMessage = this.messages[this.messages.length - 1];
    if (!aiMessage || aiMessage.isUser || !aiMessage.isStreaming) {
      return;
    }

    aiMessage.isStreaming = false;
    if (aiMessage.events?.length) {
      aiMessage.eventsCollapsed = true;
    }

    // Reveal anything already received but not yet typed out, so stopping
    // never silently drops content that was actually streamed in.
    this.flushTypingQueue();
    this.flushPlanTypingQueue();

    const hasText = !!aiMessage.text?.trim();
    if (!hasText) {
      aiMessage.text = 'Response stopped';
      aiMessage.isStoppedNotice = true;
      return;
    }

    if (this.currentTurnUserQuery) {
      this.saveChatAfterResponse(this.currentTurnUserQuery, aiMessage.text, this.isCurrentTurnNewChat, aiMessage.sources || [])
        .catch((error) => console.error('Error saving stopped response:', error));
    }
  }

  feedcardscroll(): void {
    setTimeout(() => {
      if (this.messagesContainer && this.messagesContainer.nativeElement && this.feedbackMessageId) {
        const container = this.messagesContainer.nativeElement;

        const feedbackCard = container.querySelector('.feedback-card-container');

        if (feedbackCard) {
          const feedbackRect = feedbackCard.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const containerCenter = containerRect.height / 2;
          const feedbackCenter = feedbackRect.top - containerRect.top + (feedbackRect.height / 2);
          const scrollOffset = feedbackCenter - containerCenter;
          container.scrollTo({
            top: container.scrollTop + scrollOffset,
            behavior: 'smooth'
          });
        } else {
          container.scrollTo({
            top: container.scrollTop + 400,
            behavior: 'smooth'
          });
        }
      }
    }, 100);
  }

  onMessagesScroll(): void {
    if (!this.messagesContainer) return;
    const container = this.messagesContainer.nativeElement;
    const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 8;
    this.showScrollToBottom = !atBottom;
    this.userAtBottom = atBottom;
  }

  selectQuestion(question: string): void {
    this.currentMessage = question;
    this.sendMessage();
  }

  onThumbsUp(messageId: string): void {
    const message = this.messages.find(m => m.id === messageId);
    if (message) {
      if (message.thumbsUpClicked) {
        message.thumbsUpClicked = false;
        this.showFeedbackCard = false;
        this.feedbackMessageId = null;
      } else {
        message.thumbsUpClicked = true;
        message.thumbsDownClicked = false;
        this.feedbackType = 'positive';
        this.feedbackMessageId = messageId;
        this.showFeedbackCard = true;
      }
    }
    this.scrollToBottom();
  }

  onThumbsDown(messageId: string): void {
    const message = this.messages.find(m => m.id === messageId);
    if (message) {
      if (message.thumbsDownClicked) {
        message.thumbsDownClicked = false;
        this.showFeedbackCard = false;
        this.feedbackMessageId = null;
      } else {
        message.thumbsDownClicked = true;
        message.thumbsUpClicked = false;
        this.feedbackType = 'negative';
        this.feedbackMessageId = messageId;
        this.showFeedbackCard = true;
      }
    }
    this.scrollToBottom();
  }

  onOpenFeedback(messageId: string): void {
    const message = this.messages.find(m => m.id === messageId);
    if (!message || message.isUser) {
      return;
    }

    this.feedbackMessageId = messageId;
    this.showFeedbackCard = true;
    this.scrollToBottom();
  }

  toggleEventsCollapsed(message: IChatMessage): void {
    message.eventsCollapsed = !message.eventsCollapsed;
  }

  isStepDone(message: IChatMessage, index: number): boolean {
    const events = message.events || [];
    const isLastStep = index === events.length - 1;
    return !isLastStep || !!message.text || !message.isStreaming;
  }

  hasTrace(message: IChatMessage): boolean {
    return !!(message.events?.length || message.toolSteps?.length || message.plan);
  }

  totalStepCount(message: IChatMessage): number {
    return (message.events?.length || 0) + (message.toolSteps?.length || 0) + (message.plan ? 1 : 0);
  }

  toggleToolStepExpanded(step: IToolStep): void {
    if (step.status === 'running') return;
    step.expanded = !step.expanded;
  }

  formatPlanText(text: string | undefined): string {
    if (!text) return '';
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return escaped
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br>');
  }

  formatToolArguments(args: unknown): string {
    if (args === null || args === undefined) return '';
    if (typeof args === 'string') return args;
    try {
      return JSON.stringify(args, null, 2);
    } catch {
      return String(args);
    }
  }

  toggleSourcesDropdown(messageId: string, event: MouseEvent): void {
    event.stopPropagation();
    this.openSourcesMessageId = this.openSourcesMessageId === messageId ? null : messageId;
  }

  private closeSourcesDropdown(): void {
    this.openSourcesMessageId = null;
  }

  async onCopyResponse(responseText: string): Promise<void> {
    if (!responseText?.trim()) {
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(responseText);
      } else {
        //If the browser is legacy
        const textarea = document.createElement('textarea');
        textarea.value = responseText;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      this.notificationMessage = 'Response copied to clipboard.';
      this.notificationType = 'success';
      this.showNotificationBar = true;
      this.showWarningMessage = false;

      setTimeout(() => {
        this.showNotificationBar = false;
      }, 2000);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  }
  onFeedbackClosed(): void {
    this.showFeedbackCard = false;
    if (this.feedbackMessageId) {
      const message = this.messages.find(m => m.id === this.feedbackMessageId);
      if (message) {
        message.thumbsUpClicked = false;
        message.thumbsDownClicked = false;
      }
    }
    this.feedbackMessageId = null;
  }

  onFeedbackSuccess(): void {
    this.showFeedbackSuccessNotification();
    this.showFeedbackCard = false;
    this.feedbackMessageId = null;
  }

  onFeedbackSubmitted(feedbackData: any): void {
    if (!this.feedbackMessageId || !this.currentChatId) {
      return;
    }

    const message = this.messages.find(m => m.id === this.feedbackMessageId);
    if (!message || message.isUser) {
      return;
    }

    // Use the messageIndex from the message object if available, otherwise calculate it
    let messageIndex = message.messageIndex;
    if (messageIndex === undefined || messageIndex === null) {
      // Fallback: Find the message index in the messages array (only count AI messages)
      const aiMessages = this.messages.filter(m => !m.isUser);
      messageIndex = aiMessages.indexOf(message);
    }

    const feedbackRequest: FeedbackRequestModel = {
      chatId: this.currentChatId,
      messageIndex: messageIndex,
      isLiked: feedbackData.isLiked,
      comment: feedbackData.comment || ''
    };

    const feedbackSubscription = this.chatService.submitAIFeedback(feedbackRequest).subscribe({
      next: (response: any) => {
        if (response && response.isSuccess) {
          // Update the message with feedback data
          message.feedback = {
            isLiked: feedbackData.isLiked,
            comment: feedbackData.comment
          };

          // // Reset thumbs up/down states
          // message.thumbsUpClicked = false;
          // message.thumbsDownClicked = false;

          this.showFeedbackSuccessNotification();
          console.log('Feedback submitted successfully:', feedbackRequest);
        } else {
          console.error('Failed to submit feedback:', response);
        }
      },
      error: (error: any) => {
        console.error('Error submitting feedback:', error);
        // Reset thumbs up/down states on error
        if (message) {
          message.thumbsUpClicked = false;
          message.thumbsDownClicked = false;
        }
      }
    });

    this.subscriptions.push(feedbackSubscription);
  }

  showFeedbackForMessage(messageId: string): void {
    const message = this.messages.find(m => m.id === messageId);
    if (message && !message.isUser) {
      // Show feedback card with current feedback data
      this.feedbackMessageId = messageId;
      this.showFeedbackCard = true;
    }
  }

  onBackToChat(): void {
    this.backToChat.emit();
  }

  onInferencesGenerated(): void {
    this.inferencesGenerated.emit();
  }

  startNewChat(): void {
    // Cancel any response still streaming so it can't write into the chat we're about to clear.
    this.finalizeInterruptedResponse();
    // A queued message was meant for the conversation we're leaving - discard it rather
    // than firing it into a different chat.
    this.queuedMessage = null;

    // Clear current chat
    this.revokeSecureImageUrls();
    this.messages = [];
    this.hasMessages = false;
    this.currentMessage = '';
    this.currentChatId = null; // Reset chat ID
    this.currentChatLabel = null;
    this.ChatLable = null;
    this.showScrollToBottom = false;

    this.scrollToBottom();
  }


  loadChatFromHistory(messages: ChatHistoryMessage[], chatId?: string, isLoading?: boolean, chatLabel?: string): void {
    // Cancel any response still streaming for the chat we're navigating away from, so its stream
    // can't keep writing into (and saving onto) the chat we're about to load here.
    this.finalizeInterruptedResponse();
    // A queued message was meant for the conversation we're leaving - discard it rather
    // than firing it into a different chat.
    this.queuedMessage = null;

    // If loading, show loading state and return
    if (isLoading) {
      this.revokeSecureImageUrls();
      this.revokeVoiceNoteUrls();
      stopVoiceNotePlayback();
      this.messages = [];
      this.hasMessages = false;
      this.isLoading = true;
      this.currentChatId = chatId || null;
      this.currentChatLabel = chatLabel || null;
      return;
    }

    // Stop loading state
    this.isLoading = false;

    // Clear current chat
    this.revokeSecureImageUrls();
    this.revokeVoiceNoteUrls();
    stopVoiceNotePlayback();
    this.messages = [];
    this.hasMessages = false;
    this.currentChatId = chatId || null; // Set to the selected chat ID or null
    this.currentChatLabel = chatLabel || null;
    this.showScrollToBottom = false;

    // Convert history messages to ChatMessage format
    messages.forEach((historyMessage, index) => {
      // Add user message
      const userMessage: IChatMessage = {
        id: Date.now().toString() + '_user_' + index,
        text: historyMessage.UserMessage,
        isUser: true,
        timestamp: new Date(historyMessage.CreatedDate),
        voiceNoteId: historyMessage.VoiceNoteId,
        audioSeconds: historyMessage.VoiceNoteSeconds,
        voiceContentType: historyMessage.VoiceNoteContentType
      };
      this.messages.push(userMessage);
      if (userMessage.voiceNoteId) {
        void this.attachVoiceNotePlayback(userMessage);
      }

      // Add AI response - keep img tags in text initially, they'll be resolved inline
      const aiText = historyMessage.AIResponse;
      const imageUrls = this.extractImageUrls(aiText);

      const aiMessage: IChatMessage = {
        id: Date.now().toString() + '_ai_' + index,
        text: aiText, // Keep original text with img tags - they'll be rendered by markdown pipe
        isUser: false,
        timestamp: new Date(historyMessage.CreatedDate),
        chatId: chatId,
        messageIndex: index,
        images: [], // Will be populated after blob resolution
        feedback: historyMessage.Feedback,
        // Set thumbs state based on existing feedback
        thumbsUpClicked: historyMessage.Feedback?.isLiked === true,
        thumbsDownClicked: historyMessage.Feedback?.isLiked === false,
        sources: historyMessage.Sources || undefined
      };
      this.messages.push(aiMessage);

      // Resolve secure image blobs and replace URLs inline in the text
      if (imageUrls.length > 0) {
        this.resolveHistoryImagesInline(aiMessage, imageUrls);
      }
    });
    this.hasMessages = this.messages.length > 0;
    this.scrollToBottom();
  }

  private async resolveHistoryImagesInline(aiMessage: IChatMessage, imageUrls: string[]): Promise<void> {
    // Resolve each original URL to a blob object URL
    const urlMap = new Map<string, string>();
    await Promise.all(
      imageUrls.map(async (url) => {
        const blobUrl = await this.resolveSecureImageUrl(url);
        if (blobUrl) {
          urlMap.set(url, blobUrl);
        }
      })
    );

    if (urlMap.size === 0) return;

    // Replace each original URL with the blob URL inline in the text
    urlMap.forEach((blobUrl, originalUrl) => {
      aiMessage.text = aiMessage.text.replace(originalUrl, blobUrl);
    });
    aiMessage.images = Array.from(urlMap.values());

    // Trigger change detection so the UI updates with resolved blob URLs
    this.cdr.detectChanges();
  }

  private buildChatbotRequest(userQuery: string): ChatbotStreamRequest {
    const userInfo = this.authService.getUserInfo() || (() => {
      try {
        return JSON.parse(localStorage.getItem('user_info') || '{}');
      } catch {
        return {};
      }
    })();
    const username = userInfo?.username || '';
    const userId = userInfo?.userId || '';
    const chatId = this.currentChatId || null;
    const normalizedUserId = userId || null;
    const role = this.authService.getOrganogramRoleForChat();
    const permissions = this.authService.getOrganogramPermissionsForChat();

    return {
      query: userQuery,
      user: username,
      chat_id: chatId,
      user_id: normalizedUserId,
      timestamp: new Date().toISOString(),
      tenant_name: this.tenantService.currentTenantName(),
      role,
      permissions
    };
  }

  private async persistPromptBeforeResponse(userMessage: string): Promise<string> {
    const payload = [
      {
        UserMessage: userMessage,
        AIResponse: '',
        CreatedDate: new Date().toISOString(),
        Sources: '[]' // Ensure Sources is included to prevent schema issues, even if empty
      }
    ];

    const res = await firstValueFrom(
      this.chatService.saveChat(this.currentChatId, payload, this.ChatLable ?? null)
    );

    const returnedChatId = res?.data?.chatId ?? res?.chatId;
    if (!returnedChatId) {
      throw new Error('ChatId was not returned after saving the draft message');
    }

    this.currentChatId = returnedChatId;
    return returnedChatId;
  }

  private async getAIResponse(userQuery: string, userMessageIndex: number, isNewChat: boolean): Promise<void> {
    this.isLoading = true;
    this.startThinkingCycle();

    const responseMessage: IChatMessage = {
      id: (Date.now() + 1).toString(),
      text: '',
      isUser: false,
      timestamp: new Date(),
      isStreaming: true
    };

    this.messages.push(responseMessage);
    this.scrollToBottom();

    const aiMessageIndex = this.messages.length - 1;
    let finalResponse = '';
    let finalSources: string[] = [];

    const streamSubscription = this.chatService.getChatBotResponseStream(this.buildChatbotRequest(userQuery))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (event: any) => {
          if (event.type === 'event' && this.messages[aiMessageIndex]) {
            const aiMessage = this.messages[aiMessageIndex];
            const cleanText = event.chunk.replace(/^:\s*/, '');
            if (!aiMessage.events) aiMessage.events = [];
            aiMessage.events.push(cleanText);
          }

          else if (event.type === 'plan' && this.messages[aiMessageIndex]) {
            this.enqueuePlanText(event.text, aiMessageIndex);
          }

          else if (event.type === 'toolstep' && this.messages[aiMessageIndex]) {
            const aiMessage = this.messages[aiMessageIndex];
            if (!aiMessage.toolSteps) aiMessage.toolSteps = [];
            const existing = aiMessage.toolSteps.find(s => s.callId === event.call_id);
            if (existing) {
              existing.status = event.status || existing.status;
              if (event.tool_name) existing.toolName = event.tool_name;
              if (event.arguments !== undefined) existing.arguments = event.arguments;
              if (event.result_summary) existing.resultSummary = event.result_summary;
            } else {
              aiMessage.toolSteps.push({
                callId: event.call_id,
                toolName: event.tool_name,
                status: event.status || 'running',
                arguments: event.arguments,
                resultSummary: event.result_summary
              });
            }
          }

          else if (event.type === 'content' && this.messages[aiMessageIndex]) {
            const aiMessage = this.messages[aiMessageIndex];
            if (this.hasTrace(aiMessage) && aiMessage.eventsCollapsed === undefined) {
              // Collapse the reasoning trace into a summary pill now that the real answer is arriving,
              // but keep the steps around (expandable) instead of deleting them.
              aiMessage.eventsCollapsed = true;
            }

            const formattedText = event.chunk
              .replace(
                /\[<img\s+[^>]*?src\s*=\s*['"]?([^'"\s>\]]+)['"]?[^>\]]*?>?\]/gi,
                '<img src="$1" class="markdown-image clickable-img" style="max-width:100%;height:auto;display:block;margin:8px auto;">'
              );

            finalResponse += formattedText;
            await this.appendContentChunk(formattedText, aiMessageIndex);
            this.followScrollIfAtBottom();

          } else if (event.type === 'sources' && this.messages[aiMessageIndex]) {
            try {
              const parsedSources = JSON.parse(event.chunk);
              if (Array.isArray(parsedSources)) {
                this.messages[aiMessageIndex].sources = parsedSources;
                finalSources = parsedSources;
              }
            } catch (e) {
              console.error('Error parsing sources:', e);
            }
          } else if (event.type === 'ChatLable') {
            this.ChatLable = event.chunk;   // ✅ use 'chunk'
          }
          else if (event.type === 'metadata' && event.chatId) {
            this.currentChatId = this.currentChatId || event.chatId;
          }
        },
        complete: async () => {
          this.isLoading = false;
          this.activeStreamSubscription = null;
          this.stopThinkingCycle();
          this.closeSourcesDropdown();

          const aiMessage = this.messages[aiMessageIndex];
          if (aiMessage && finalResponse.trim()) {
            aiMessage.isStreaming = false;
            if (this.hasTrace(aiMessage) && aiMessage.eventsCollapsed === undefined) {
              aiMessage.eventsCollapsed = true;
            }
            try {
              await this.saveChatAfterResponse(userQuery, finalResponse, isNewChat, finalSources);
            } catch (error) {
              console.error('Error saving completed chat response:', error);
            }
          } else {
            this.typingQueue = [];
            this.planTypingQueue = [];
            if (aiMessage) {
              aiMessage.text = 'Sorry, there was an error getting the response.';
              aiMessage.isStreaming = false;
            }
            this.clearChatAfterFailedTurn();
          }
          this.trySendQueuedMessage();
        },
        error: (error) => {
          console.error('Stream error:', error);
          this.typingQueue = [];
          this.planTypingQueue = [];
          const aiMessage = this.messages[aiMessageIndex];
          if (aiMessage) {
            if (this.hasTrace(aiMessage)) {
              aiMessage.eventsCollapsed = true;
            }
            aiMessage.text = 'Sorry, there was an error getting the response.';
            aiMessage.isStreaming = false;
          }
          this.isLoading = false;
          this.activeStreamSubscription = null;
          this.stopThinkingCycle();
          this.clearChatAfterFailedTurn();
          this.trySendQueuedMessage();
        }
      });

    this.activeStreamSubscription = streamSubscription;
    this.subscriptions.push(streamSubscription);
  }


  private async saveChatAfterResponse(userMessage: string, aiResponse: string, isNewChat: boolean, sources: string[] = []): Promise<void> {
    const resolvedLabel =
      this.ChatLable ??
      this.currentChatLabel ??
      this.deriveChatLabel(aiResponse);

    if (!this.currentChatId) {
      throw new Error('Missing chatId before saving AI response');
    }

    const updateExistingDraft = isNewChat || this.persistVoiceAsUpdate;
    this.persistVoiceAsUpdate = false;

    if (updateExistingDraft) {
      const updatePayload: UpdateChatModel = {
        chatId: this.currentChatId,
        chatLabel: resolvedLabel,
        aiResponse,
        sources: sources.length > 0 ? JSON.stringify(sources) : null
      };

      const res: UpdateChatResponse = await firstValueFrom(
        this.chatService.updateChat(updatePayload)
      );

      const returnedChatId = res?.data?.chatId ?? this.currentChatId;
      const returnedLabel = res?.data?.chatLabel ?? resolvedLabel;

      this.currentChatId = returnedChatId;
      this.currentChatLabel = returnedLabel;

      if (returnedLabel) {
        this.chatSaved.emit({
          chatId: returnedChatId,
          label: returnedLabel
        });
      }

      return;
    }

    const payload = [
      {
        UserMessage: userMessage,
        AIResponse: aiResponse,
        Sources: sources.length > 0 ? JSON.stringify(sources) : null
      }
    ];

    const res = await firstValueFrom(
      this.chatService.saveChat(
        this.currentChatId,
        payload,
        resolvedLabel
      )
    );

    const returnedChatId = res?.data?.chatId ?? res?.chatId ?? this.currentChatId;
    this.currentChatId = returnedChatId;
    this.currentChatLabel = resolvedLabel;

    this.chatSaved.emit({
      chatId: returnedChatId,
      label: resolvedLabel
    });
  }

  private deriveChatLabel(text: string): string {
    if (!text?.trim()) {
      return 'New Chat';
    }

    const markdownHeadingMatch = text.match(/^#{1,3}\s+(.+)$/m);
    if (markdownHeadingMatch?.[1]) {
      return markdownHeadingMatch[1].trim();
    }

    const htmlHeadingMatch = text.match(/<h[1-3][^>]*>(.+?)<\/h[1-3]>/i);
    if (htmlHeadingMatch?.[1]) {
      return htmlHeadingMatch[1].replace(/<[^>]*>/g, '').trim();
    }

    const boldMatch = text.match(/\*\*(.+?)\*\*/);
    if (boldMatch?.[1]) {
      const boldText = boldMatch[1].trim();
      if (boldText.length > 10 && boldText.length < 100) {
        return boldText;
      }
    }

    const quoteMatch = text.match(/"([^"]{10,100})"/);
    if (quoteMatch?.[1]) {
      return quoteMatch[1].trim();
    }

    const colonMatch = text.match(/^([^:\n]{10,100}):/m);
    if (colonMatch?.[1]) {
      return colonMatch[1].trim();
    }

    const firstSentence = text.split('.')[0].trim();
    if (firstSentence.length > 10 && firstSentence.length <= 100) {
      return firstSentence;
    }

    return text.trim().slice(0, 50);
  }

  private async appendContentChunk(chunk: string, aiMessageIndex: number): Promise<void> {
    const aiMessage = this.messages[aiMessageIndex];
    if (!aiMessage) return;

    let cleanChunk = chunk;
    const imageUrls = this.extractImageUrls(chunk);
    for (const url of imageUrls) {
      const blobUrl = await this.resolveSecureImageUrl(url);
      if (blobUrl) {
        cleanChunk = cleanChunk.replace(url, blobUrl);
        // Track the resolved (authenticated) blob URL, not the raw storage
        // URL - the raw one has no SAS token and 404s if opened directly.
        aiMessage.images = [...(aiMessage.images || []), blobUrl];
      }
    }

    this.enqueueTypingChunk(cleanChunk, aiMessageIndex);
  }

  /**
   * Reveals content word-by-word instead of dumping a whole network chunk in
   * at once, matching how Claude/ChatGPT visually stream. This is queue-based
   * rather than per-chunk: a single drain loop consumes tokens continuously,
   * and new chunks just push more tokens onto it. A prior version awaited a
   * per-character animation inline for each chunk and resolved that promise
   * without actually finishing the animation for chunks after the first -
   * that mismatch is what caused the answer to sometimes stall or jump in.
   * Queueing sidesteps that: appendContentChunk() never blocks on typing, and
   * bursty chunks just queue up smoothly instead of racing each other.
   */
  private typingQueue: string[] = [];
  private isTypingActive = false;
  private typingTargetIndex: number | null = null;

  private enqueueTypingChunk(chunk: string, aiMessageIndex: number): void {
    if (!chunk) return;

    if (this.typingTargetIndex !== aiMessageIndex) {
      this.typingQueue = [];
      this.typingTargetIndex = aiMessageIndex;
    }

    // Split into word + trailing-whitespace tokens so words reveal as whole
    // units (fast, chat-like) rather than character by character.
    const tokens = chunk.match(/\S+\s*|\s+/g) || [chunk];
    this.typingQueue.push(...tokens);

    if (!this.isTypingActive) {
      this.drainTypingQueue();
    }
  }

  private async drainTypingQueue(): Promise<void> {
    this.isTypingActive = true;
    while (this.typingQueue.length > 0) {
      const aiMessage = this.messages[this.typingTargetIndex!];
      if (!aiMessage) break;

      const token = this.typingQueue.shift()!;
      aiMessage.text += token;
      this.followScrollIfAtBottom();

      await new Promise(resolve => setTimeout(resolve, 12));
    }
    this.isTypingActive = false;
  }

  /** Instantly reveals whatever's left in the queue - used when a stream is
   * stopped/errors, so nothing already received gets silently dropped. */
  private flushTypingQueue(): void {
    if (this.typingTargetIndex !== null && this.typingQueue.length > 0) {
      const aiMessage = this.messages[this.typingTargetIndex];
      if (aiMessage) {
        aiMessage.text += this.typingQueue.join('');
      }
    }
    this.typingQueue = [];
  }

  /** Same word-reveal treatment as the response text, but for the "Thinking"
   * plan - it currently arrives as one full block from the backend rather
   * than incrementally, so this is what makes it animate in at all. */
  private planTypingQueue: string[] = [];
  private isPlanTypingActive = false;
  private planTypingTargetIndex: number | null = null;

  private enqueuePlanText(text: string, aiMessageIndex: number): void {
    if (!text) return;

    const aiMessage = this.messages[aiMessageIndex];
    const isNewTarget = this.planTypingTargetIndex !== aiMessageIndex;

    if (isNewTarget) {
      this.planTypingQueue = [];
      this.planTypingTargetIndex = aiMessageIndex;
      if (aiMessage) aiMessage.plan = '';
    } else if (this.planTypingQueue.length > 0 || aiMessage?.plan) {
      // The agent can replan more than once per turn - each plan_generated
      // event was landing tacked directly onto the previous one's last
      // word with no space at all, running distinct thoughts together
      // into one unreadable sentence. Separate revisions with a paragraph
      // break instead of concatenating them raw.
      this.planTypingQueue.push('\n\n');
    }

    const tokens = text.match(/\S+\s*|\s+/g) || [text];
    this.planTypingQueue.push(...tokens);

    if (!this.isPlanTypingActive) {
      this.drainPlanTypingQueue();
    }
  }

  private async drainPlanTypingQueue(): Promise<void> {
    this.isPlanTypingActive = true;
    while (this.planTypingQueue.length > 0) {
      const aiMessage = this.messages[this.planTypingTargetIndex!];
      if (!aiMessage) break;

      const token = this.planTypingQueue.shift()!;
      aiMessage.plan = (aiMessage.plan || '') + token;
      this.followScrollIfAtBottom();

      await new Promise(resolve => setTimeout(resolve, 12));
    }
    this.isPlanTypingActive = false;
  }

  private flushPlanTypingQueue(): void {
    if (this.planTypingTargetIndex !== null && this.planTypingQueue.length > 0) {
      const aiMessage = this.messages[this.planTypingTargetIndex];
      if (aiMessage) {
        aiMessage.plan = (aiMessage.plan || '') + this.planTypingQueue.join('');
      }
    }
    this.planTypingQueue = [];
  }

  openImagePreview(imageUrl: string) {
    this.previewImage = imageUrl;
  }
  closePreview() {
    this.previewImage = null;
  }
  // downloadImage() {
  //   if (!this.previewImage) return;
  //   const link = document.createElement('a');
  //   link.href = this.previewImage;
  //   link.download = this.previewImage.split('/').pop() || 'image.png';
  //   link.click();
  // }
  async downloadImage() {
    if (!this.previewImage) return;
    await this.downloadImageUrl(this.previewImage);
  }

  async onDownloadMessageImages(message: IChatMessage): Promise<void> {
    const images = message.images || [];
    for (let i = 0; i < images.length; i++) {
      await this.downloadImageUrl(images[i], images.length > 1 ? `image-${i + 1}.png` : undefined);
    }
  }

  private async downloadImageUrl(imageUrl: string, filename?: string): Promise<void> {
    try {
      // Fetch the image as a blob
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      // Create object URL from blob
      const url = window.URL.createObjectURL(blob);

      // Create and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || (imageUrl.startsWith('blob:')
        ? 'secure-image.png'
        : imageUrl.split('/').pop() || 'image.png');
      document.body.appendChild(link); // Some browsers need this
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback: open in new tab
      window.open(imageUrl, '_blank');
    }
  }
  ngOnDestroy(): void {
    // Emit to cancel any ongoing streams
    this.destroy$.next();
    this.destroy$.complete();

    // Stop thinking cycle to prevent memory leaks
    this.stopThinkingCycle();

    // Unsubscribe from all subscriptions to prevent memory leaks
    this.subscriptions.forEach(subscription => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe();
      }
    });
    this.subscriptions = [];

    this.revokeSecureImageUrls();
    this.revokeVoiceNoteUrls();
    stopVoiceNotePlayback();
    this.voiceRecorder.destroy();
  }
  @ViewChild('messageInput', { static: false }) messageInput!: ElementRef<HTMLTextAreaElement>;

  // currentMessage: string = '';
  // isLoading: boolean = false;

  ngAfterViewInit() {
    this.adjustTextareaHeight();
    setTimeout(() => this.checkPosition(), 0);
  }

  onInput() {
    this.adjustTextareaHeight();
  }

  private adjustTextareaHeight() {
    const textarea = this.messageInput?.nativeElement;
    if (!textarea) return;

    // Reset height to allow shrinking
    textarea.style.height = 'auto';

    // Calculate the line height
    const lineHeight = 1.4 * 14; // 1.4 line-height * 14px font-size = 19.6px per line
    const styles = window.getComputedStyle(textarea);
    const paddingTop = parseFloat(styles.paddingTop) || 0;
    const paddingBottom = parseFloat(styles.paddingBottom) || 0;
    const minHeight = lineHeight + paddingTop + paddingBottom; // 1 line + padding
    const maxHeight = lineHeight * 9 + paddingTop + paddingBottom; // 9 lines + padding, then scrolls internally
    // Keep first row fixed
    textarea.style.height = minHeight + 'px';
 
    // Expand only when content exceeds first row
    const neededHeight = textarea.scrollHeight;
    if (neededHeight > minHeight + 1) {
      textarea.style.height = Math.min(neededHeight, maxHeight) + 'px';
    }
 
    // Enable/disable scrolling based on whether we've hit max height
    textarea.style.overflowY = neededHeight > maxHeight ? 'auto' : 'hidden';
  } 

  private clearChatAfterFailedTurn(): void {
    if (this.persistVoiceAsUpdate) {
      this.persistVoiceAsUpdate = false;
      return;
    }
    this.currentChatId = null;
    this.currentChatLabel = null;
    this.ChatLable = null;
  }

  private revokeVoiceNoteUrls(): void {
    this.voiceLoadToken++;
    this.voiceObjectUrls.forEach((url) => URL.revokeObjectURL(url));
    this.voiceObjectUrls = [];
  }

  private trackVoiceUrl(url: string): string {
    this.voiceObjectUrls.push(url);
    return url;
  }

  private async attachVoiceNotePlayback(message: IChatMessage): Promise<void> {
    const id = message.voiceNoteId;
    if (!id) return;
    const token = this.voiceLoadToken;
    try {
      const blob = await firstValueFrom(this.chatService.getVoiceNote(id));
      if (token !== this.voiceLoadToken || message.voiceNoteId !== id) return;
      message.audioUrl = this.trackVoiceUrl(URL.createObjectURL(blob));
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading voice note:', error);
    }
  }

  liveBarHeight(level: number): number {
    return voiceBarHeight(level);
  }

  get recordClock(): string {
    const safe = Math.max(0, Math.floor(this.recordSeconds || 0));
    const minutes = Math.floor(safe / 60);
    const seconds = safe % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  async startVoiceRecording(): Promise<void> {
    if (this.isRecording || this.voiceStartPending || this.isLoading || this.isUploadingVoice) return;
    this.voiceError = null;
    this.voiceStartPending = true;
    stopVoiceNotePlayback();
    let result: VoiceRecording | VoiceRecorderError;
    try {
      result = await this.voiceRecorder.start((seconds, bars) => {
        this.isRecording = true;
        this.recordSeconds = seconds;
        this.liveBars = bars;
        this.cdr.detectChanges();
      });
    } catch {
      this.voiceError = this.voiceLabels.voiceError;
      return;
    } finally {
      this.voiceStartPending = false;
      this.isRecording = false;
      this.recordSeconds = 0;
      this.liveBars = flatWave();
      // The textarea is destroyed/recreated by *ngIf while recording, so its inline
      // height style (set by adjustTextareaHeight) is lost. Recompute it once the
      // fresh element is back in the DOM instead of leaving it at the browser's
      // default (taller) textarea height.
      setTimeout(() => this.adjustTextareaHeight(), 0);
    }
    if (typeof result === 'string') {
      this.voiceError = this.voiceRecorderMessage(result);
      return;
    }
    await this.publishVoiceRecording(result);
  }

  sendVoiceRecording(): void {
    this.voiceRecorder.finish(false);
  }

  cancelVoiceRecording(): void {
    this.voiceRecorder.finish(true);
  }

  private voiceRecorderMessage(result: VoiceRecorderError): string | null {
    if (result === 'discarded') return null;
    if (result === 'mic-denied') return this.voiceLabels.voiceMicDenied;
    if (result === 'too-short') return this.voiceLabels.voiceTooShort;
    if (result === 'unsupported') return this.voiceLabels.voiceUnsupported;
    return this.voiceLabels.voiceError;
  }

  private async publishVoiceRecording(recording: VoiceRecording): Promise<void> {
    const audioUrl = this.trackVoiceUrl(URL.createObjectURL(recording.blob));
    const userMessage: IChatMessage = {
      id: Date.now().toString(),
      text: '',
      isUser: true,
      timestamp: new Date(),
      audioUrl,
      waveform: recording.waveform,
      audioSeconds: recording.seconds,
      pendingTranscript: true,
      voiceContentType: (recording.blob.type || 'audio/webm').split(';')[0]
    };
    this.messages.push(userMessage);
    this.hasMessages = true;
    this.userAtBottom = true;
    this.scrollToBottom();
    this.isUploadingVoice = true;

    try {
      const response = await firstValueFrom(this.chatService.uploadVoiceNote(recording.blob, recording.seconds));
      const transcript = response?.data?.transcript?.trim();
      if (!response?.isSuccess || !transcript || !response.data?.voiceNoteId) {
        this.dropVoiceBubble(userMessage, response?.message || this.voiceLabels.voiceNoSpeech);
        return;
      }

      userMessage.text = transcript;
      userMessage.pendingTranscript = false;
      userMessage.voiceNoteId = response.data.voiceNoteId;
      userMessage.voiceContentType = response.data.contentType || userMessage.voiceContentType;
      if (response.data.durationSeconds > 0) {
        userMessage.audioSeconds = response.data.durationSeconds;
      }
      await this.persistVoiceDraft(userMessage);
      const userMessageIndex = this.messages.indexOf(userMessage);
      this.persistVoiceAsUpdate = true;
      this.dispatchAIResponse(transcript, userMessageIndex);
    } catch (error) {
      console.error('Error sending voice note:', error);
      const httpMessage = (error as { error?: { message?: string } })?.error?.message;
      this.dropVoiceBubble(userMessage, httpMessage || this.voiceLabels.voiceError);
    } finally {
      this.isUploadingVoice = false;
    }
  }

  private async persistVoiceDraft(message: IChatMessage): Promise<void> {
    const payload = [
      {
        UserMessage: message.text,
        AIResponse: '',
        CreatedDate: message.timestamp.toISOString(),
        Sources: '[]',
        VoiceNoteId: message.voiceNoteId,
        VoiceNoteSeconds: message.audioSeconds || 0,
        VoiceNoteContentType: message.voiceContentType || 'audio/webm'
      }
    ];
    const res = await firstValueFrom(
      this.chatService.saveChat(this.currentChatId, payload, this.ChatLable ?? this.currentChatLabel)
    );
    const returnedChatId = res?.data?.chatId ?? res?.chatId;
    if (!returnedChatId) {
      throw new Error('ChatId was not returned after saving the voice note');
    }
    this.currentChatId = returnedChatId;
  }

  private dropVoiceBubble(message: IChatMessage, errorText: string): void {
    if (message.audioUrl) {
      URL.revokeObjectURL(message.audioUrl);
      this.voiceObjectUrls = this.voiceObjectUrls.filter((url) => url !== message.audioUrl);
    }
    this.messages = this.messages.filter((item) => item !== message);
    this.hasMessages = this.messages.length > 0;
    this.voiceError = errorText;
    this.persistVoiceAsUpdate = false;
    this.cdr.detectChanges();
  }

  sendMessage(): void {
    if (!this.currentMessage.trim() || this.isLoading || this.isUploadingVoice || this.isRecording) return;
    this.voiceError = null;

    this.isLoading = true;

    const userMessage: IChatMessage = {
      id: Date.now().toString(),
      text: this.currentMessage,
      isUser: true,
      timestamp: new Date()
    };

    this.messages.push(userMessage);
    this.hasMessages = true;
    const userMessageIndex = this.messages.length - 1;
    const userQuery = this.currentMessage;
    this.currentMessage = '';

    // Reset textarea height after clearing message
    setTimeout(() => this.adjustTextareaHeight(), 0);

    this.userAtBottom = true;
    this.scrollToBottom();

    this.dispatchAIResponse(userQuery, userMessageIndex);
  }

  /**
   * Everything after a user message is already showing in the transcript: persist it
   * (for a brand-new chat) and start streaming the answer. Shared by sendMessage() and
   * by a queued message once the response ahead of it finishes.
   */
  private dispatchAIResponse(userQuery: string, userMessageIndex: number): void {
    const isNewChat = !this.currentChatId;
    this.isCurrentTurnNewChat = isNewChat;
    this.currentTurnUserQuery = userQuery;

    if (isNewChat) {
      this.persistPromptBeforeResponse(userQuery)
        .then(() => this.getAIResponse(userQuery, userMessageIndex, true))
        .catch((error) => {
          console.error('Error saving prompt before AI response:', error);
          this.messages.splice(userMessageIndex, 1);
          this.hasMessages = this.messages.length > 0;
          this.isLoading = false;
          this.stopThinkingCycle();
          this.currentChatId = null;
          this.currentChatLabel = null;
          this.ChatLable = null;
        });
      return;
    }

    this.getAIResponse(userQuery, userMessageIndex, false)
      .catch((error) => {
        console.error('Error getting AI response:', error);
        this.typingQueue = [];
        this.planTypingQueue = [];
        const aiMessage = this.messages[this.messages.length - 1];
        if (aiMessage) {
          aiMessage.text = 'Sorry, there was an error getting the response.';
          aiMessage.isStreaming = false;
        }
        this.isLoading = false;
        this.stopThinkingCycle();
        this.currentChatId = null;
        this.currentChatLabel = null;
        this.ChatLable = null;
      });
  }
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (this.isUploadingVoice || this.isRecording) return;
      // While a response is streaming, hold what's typed instead of sending it -
      // it fires on its own once the current response finishes, one at a time.
      if (this.isLoading) {
        this.queueMessage();
      } else {
        this.sendMessage();
      }
    }
    // Allow Shift+Enter for new lines
  }
  onContentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.tagName === 'IMG' && target.classList.contains('clickable-img')) {
      this.selectedImage = target.getAttribute('src');
    }
  }
}